import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';

import { IntellimixType } from '@shure/device-ui/intellimix/data-access';
import { DeviceUiIntellimixDeviceBehaviorPlugin } from '@shure/device-ui/intellimix/data-access';
import { DeviceUiLoudspeakerDeviceBehaviorPlugin } from '@shure/device-ui/loudspeaker/data-access';
import { FactoryResetText } from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsFactoryResetBehaviorPlugin } from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsGeneralBehaviorPlugin } from '@shure/device-ui/settings/general/data-access';
import { DspType } from '@shure/device-ui/shared/dsp/data-access';
import { DeviceUiPage, DeviceUiRoute } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { DeviceModel, EthernetPowerSourceType } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin, DeviceNameBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import { CoverageImage, CoverageImageProvider } from '@shure/shared/angular/features/device-coverage/ui';
import {
	LightsSettingsDeviceBehaviorPlugin,
	LightsSettingsSupportType
} from '@shure/shared/angular/features/device-lights-settings/data-access';
import {
	LightsImage,
	LightsImageProvider
} from '@shure/shared/angular/features/device-lights-settings/feature-device-image-next';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class Mxa902DeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSettingsFactoryResetBehaviorPlugin,
		DeviceUiLoudspeakerDeviceBehaviorPlugin,
		LightsSettingsDeviceBehaviorPlugin,
		DeviceUiIntellimixDeviceBehaviorPlugin,
		DeviceUiSettingsGeneralBehaviorPlugin,
		DeviceNameBehaviorPlugin,
		LightsImageProvider,
		CoverageImageProvider
{
	public readonly models: DeviceModel[] = [DeviceModel.MXA902S];

	constructor(protected readonly transloco: TranslocoService) {}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		const pages = [
			{
				route: DeviceUiRoute.Intellimix,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.intellimix')
			},
			{
				route: DeviceUiRoute.Loudspeaker,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.loudspeaker')
			},
			{
				route: DeviceUiRoute.Presets,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.presets')
			},
			{
				route: DeviceUiRoute.Settings,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.settings')
			}
		];

		return of(pages);
	}

	public getDefaultRoute(_model: DeviceModel): DeviceUiRoute | undefined {
		return DeviceUiRoute.Intellimix;
	}

	public getResetText(_model: DeviceModel): FactoryResetText {
		return {
			tab$: this.transloco.selectTranslate('scDevUiSharedDevBehavior.settings.factory-reset'),
			title: this.transloco.translate('scDevUiSharedDevBehavior.settings.factory-reset'),
			button: this.transloco.translate(
				'scDevUiSharedDevBehavior.settings.factory-reset-page.restore-factory-defaults'
			)
		};
	}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.Supported;
	}

	public getLightsImage(): LightsImage {
		return {
			imageUrl: 'device-mxa9xx-s.svg'
		};
	}

	public getCoverageImage(): CoverageImage {
		return {
			live: 'device-mxa902-s-back.svg',
			virtual: 'device-mxa902-s-back-line.svg',
			isImageRound: false
		};
	}

	public showPatentInfo(): boolean {
		return true;
	}

	public showGeneralSettingsPoeBanner(_model: DeviceModel, ethernetPowerSource: EthernetPowerSourceType): boolean {
		return ethernetPowerSource === EthernetPowerSourceType.Poe;
	}

	public showDanteOutput(): boolean {
		return false;
	}

	public getIntellimixType(_model: DeviceModel): IntellimixType | undefined {
		return IntellimixType.Advanced;
	}

	public revertAutomixerDefaultsCapability(_model: DeviceModel): boolean {
		return false;
	}

	public hideAgcMetering(_model: DeviceModel): boolean {
		return true;
	}

	public disableLoudspeakerOnPoE(): boolean {
		return true;
	}

	public getIntellimixChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> | undefined {
		return new Map<DspType, number>([
			[DspType.Agc, 1],
			[DspType.Aec, 2],
			[DspType.Nr, 3],
			[DspType.Peq, 4],
			[DspType.Compressor, 5],
			[DspType.Delay, 6]
		]);
	}

	public getLoudspeakerChannelDspSortingMap(): Map<DspType, number> | undefined {
		return new Map<DspType, number>([
			[DspType.Delay, 1],
			[DspType.SignalGenerator, 2],
			[DspType.Peq, 3],
			[DspType.Compressor, 4]
		]);
	}

	public getDeviceNameMaxLength(_model: DeviceModel): number | undefined {
		return 31;
	}

	public showAecReferenceInfoIcon(_modeL: DeviceModel): boolean {
		return true;
	}

	public canResetCoverageAreas(): boolean {
		return true;
	}
}
